import { create } from 'zustand'
import { Assignation } from '../../../context/types'
import { AppStatus, BinRefs, QRankedStms } from './types'
import {
    getHighlightedBars,
    getOverfilledBuckets,
    getUnderfilledBuckets,
    initBins,
    pickReplacement,
    rankedStms2buckets,
} from './helper'
import { doShuffle, doShuffleWithSeed } from '../../../context/helpers/arrays'
import { getStatements } from '../../../i18n/Statements'
import { QSortV2Params } from '../../../parameters/types'
import { BinsStore, phase2index } from './types'
import {
    DeckSizeType,
    initialBinStore,
    maxBucketCapacities,
    stepDistribution,
    stepDistributions42,
} from './constants'
import { ALL_CATS_NO_MACHT } from '../../../context/helpers/stms'
import { ALL_CATS } from '../../../utils/values/cats'
import _ from 'lodash'
import { byDiversityAsc, debugStm, toSpliced } from './utils'
import { computePages } from '../hooks/usePaginatedBin'
import { logException } from '../../../services/sentryLogger'
import { TranslatorFn } from '../../../i18n/useTranslation'
import { maskOutOfRange } from '../../../parameters/utils'
import { WarningMessage } from '../components/warningMessage'
import { ComponentPropsWithRef } from 'react'
import { parseScreenParam } from './utils'

// ====== ZUSTAND ======
// https://github.com/pmndrs/zustand
// https://medium.com/@freeyeon96/zustand-react-query-new-state-management-7aad6090af56
// https://github.com/pmndrs/zustand
export const useBinsStore = create<BinsStore>((set, get) => ({
    setPage: (page) => set({ page }),
    setMaxPerPage: (maxPerPage) => set({ maxPerPage }),
    selectBin: (binRef) => set({ selectedBinRef: binRef }),
    assignBin: (stmValueKey, destBinRef, srcBinRef) =>
        set((state) => {
            const sourceBinRef =
                srcBinRef ?? state.selectedBinRef ?? 'to_assign'
            const selectedBin = state.bins[sourceBinRef]
            const stmIndex = selectedBin.stms.findIndex(
                (s) => String(s.valueKey) === stmValueKey
            )
            if (stmIndex === -1) {
                logException('assignBin', 'stm not found :' + stmValueKey)
                return state
            }
            const targetStm = selectedBin.stms[stmIndex]
            const destBin = state.bins[destBinRef]
            const alreadyInBin = destBin.stms.some((s) => s.id === targetStm.id)
            if (alreadyInBin) {
                logException(
                    'assignBin',
                    'already in bin :' + debugStm(targetStm)
                )
                return state
            }

            // 1. Remove the statement from "selectedBinRef"
            // 2. Modify the to_assign bin to maximize diversity
            let stms2replace: Assignation[] = []
            if (sourceBinRef === 'to_assign' && get().page === 0) {
                // This applied only for the first page of the to_assign bin !!!
                // Otherwise, we need to compute the pageInfo for each page
                // and the sorting of the stmsUnExplored is not relevant anymore
                const pageInfo = computePages(
                    selectedBin.stms.slice(),
                    0,
                    get().maxPerPage
                )
                pageInfo.stmsForPage = pageInfo.stmsForPage.filter(
                    (s) => s.id !== targetStm.id
                )
                const replacement = pickReplacement(
                    targetStm,
                    pageInfo.stmsForPage,
                    pageInfo.stmsUnExplored
                )
                const stms2keep = selectedBin.stms.slice(
                    stmIndex + 1,
                    get().maxPerPage
                )
                const sortedUnxplored = pageInfo.stmsUnExplored
                    .filter((s) => s.id !== replacement?.id)
                    .sort(byDiversityAsc)
                // replacement at the bottom of page 0
                stms2replace = replacement
                    ? [...stms2keep, replacement, ...sortedUnxplored]
                    : [...stms2keep, ...sortedUnxplored]
            }

            const newSelectedStms = toSpliced(
                selectedBin.stms,
                stmIndex,
                stms2replace.length + 1, // to delete
                ...stms2replace
            )

            return {
                bins: {
                    ...state.bins,
                    [sourceBinRef]: {
                        ...selectedBin,
                        stms: newSelectedStms,
                    },
                    [destBinRef]: {
                        ...destBin,
                        stms: [...destBin.stms, targetStm],
                    },
                },
            }
        }),
    assignAllRnd: () =>
        set((state) => {
            const newBins = { ...state.bins }
            const toAssignStms = [...newBins.to_assign.stms]

            // Clear the to_assign bin
            newBins.to_assign = {
                ...newBins.to_assign,
                stms: [],
            }

            // Randomly assign each statement to one of the three bins
            toAssignStms.forEach((stm) => {
                // Get available bins that haven't reached their maximum capacity
                const availableBins: BinRefs[] = (
                    [
                        'least_important',
                        'moderately_important',
                        'most_important',
                    ] as BinRefs[]
                ).filter(
                    (binRef) =>
                        newBins[binRef].stms.length < newBins[binRef].max
                )

                // If no available bins, we can't assign more statements
                if (availableBins.length === 0) {
                    console.warn(
                        'No available bins for random assignment - all bins are at maximum capacity'
                    )
                    return
                }

                // Randomly choose one of the available bins
                const randomIndex = Math.floor(
                    Math.random() * availableBins.length
                )
                const targetBinRef = availableBins[randomIndex]

                // Add the statement to the target bin
                newBins[targetBinRef] = {
                    ...newBins[targetBinRef],
                    stms: [...newBins[targetBinRef].stms, stm],
                }
            })

            return { bins: newBins }
        }),
    //
    ...initialBinStore,
    bins: initBins(
        getStatements(),
        stepDistributions42[0],
        initialBinStore.maxPerPage
    ),
    //
    initBinsStore: (
        t: TranslatorFn,
        qParams: QSortV2Params,
        langIndex,
        shuffled = true
    ) => {
        console.debug('init=', {
            qParams,
            langIndex,
            shuffled,
        })

        const { hasPower, nSets, stmsPerScreen, nBucket } = qParams
        const nValuesPerSet = hasPower ? 14 : 13
        const nStm = (nSets * nValuesPerSet) as DeckSizeType
        let allStms: Assignation[] = getStatements(langIndex)
        const maxPerPage = parseScreenParam(stmsPerScreen).nStm

        if (!hasPower) {
            allStms = allStms.filter((a) => a.category !== 'macht')
        }

        if (nSets === 2) {
            const subCats = doShuffle(hasPower ? ALL_CATS : ALL_CATS_NO_MACHT)
            const twoPerType = subCats
                .map((cat) => allStms.filter((s) => s.category === cat))
                .slice(0, nSets)
            allStms = _.flatten(twoPerType)
        }

        const shuffledStms = shuffled
            ? (doShuffle(allStms) as unknown as Assignation[])
            : (doShuffleWithSeed(allStms) as unknown as Assignation[])

        if (shuffledStms.length !== nStm) {
            throw new Error('Inconsistent set size')
        }

        set({
            ...initialBinStore,
            ...qParams,
            stms2rank: shuffledStms,
            nStm,
            maxPerPage,
            // Real init=
            bins: initBins(shuffledStms, stepDistribution[nStm][0], maxPerPage),
            title: t('ranker_title'),
            histogramCount: maxBucketCapacities[nBucket],
        })
        // console.debug('init=', get().stms2rank)
    },
    onClickToNext: (t: TranslatorFn) => {
        set((s) => {
            const {
                // params
                reflectionMode,
                nBucket,
                hasMidBucketRanking,
                promptRankMidBucket,
                // state
                status: fromStatus,
                rankedStms,
                finalBuckets,
                bins: rankedBins,
                nStm,
                maxPerPage,
            } = s

            const hasFinalBuckets = finalBuckets?.[0]?.length
            let newRankedStms = [] as unknown as QRankedStms
            const effectiveNBuckets =
                nBucket === 7 && hasMidBucketRanking === true ? 9 : nBucket
            console.debug('onClickToNext', { fromStatus }, 'params=', {
                reflectionMode,
                nBucket,
                hasMidBucketRanking,
                promptRankMidBucket,
            })
            switch (fromStatus) {
                // case 0: cf. initBinsStore()
                case 'S1_42':
                    return {
                        status: 'S2_11_LOW',
                        rankedStms: [rankedBins, null, null, null],
                        selectedBinRef: 'to_assign',
                        bins: initBins(
                            rankedBins.least_important.stms,
                            stepDistribution[nStm][phase2index.p11low],
                            maxPerPage
                        ),
                        // UI
                        title: t('further_ranking', [
                            String(rankedBins.least_important.stms.length),
                            t('least_important').toLowerCase(),
                        ]),
                        histogramCount: maskOutOfRange(
                            0,
                            2,
                            maxBucketCapacities[s.nBucket]
                        ),
                    }
                case 'S2_11_LOW':
                    if (nBucket === 7) {
                        return {
                            status: 'S3_11_HIGH',
                            rankedStms: [
                                s.rankedStms[0],
                                rankedBins,
                                null,
                                null,
                            ],
                            selectedBinRef: 'to_assign',
                            bins: initBins(
                                s.rankedStms[0]?.most_important.stms ?? [],
                                stepDistribution[nStm][phase2index.p11high],
                                maxPerPage
                            ),
                            title: t('further_ranking2', [
                                String(
                                    s.rankedStms[0]?.most_important.stms.length
                                ),
                                t('most_important').toLowerCase(),
                            ]),
                            histogramCount: maskOutOfRange(
                                6,
                                8,
                                maxBucketCapacities[s.nBucket]
                            ),
                        }
                    } else if (nBucket === 9) {
                        return {
                            status: 'S3_20_MID',
                            rankedStms: [
                                s.rankedStms[0],
                                rankedBins,
                                null,
                                null,
                            ],
                            selectedBinRef: 'to_assign',
                            bins: initBins(
                                s.rankedStms[0]?.moderately_important.stms ??
                                    [],
                                stepDistribution[nStm][phase2index.p20mid],
                                maxPerPage
                            ),
                            // UI
                            title: t('further_ranking1', [
                                String(
                                    s.rankedStms[0]?.moderately_important.stms
                                        .length
                                ),
                                t('moderately_important').toLowerCase(),
                            ]),
                            histogramCount: maskOutOfRange(
                                3,
                                5,
                                maxBucketCapacities[s.nBucket]
                            ),
                        }
                    }
                    break
                case 'S3_20_MID':
                    if (nBucket !== 9) {
                        console.warn(
                            'S3_20_MID cannot be reached with nBucket !== 9'
                        )
                        return s
                    }
                    return {
                        status: 'S4_11_HIGH',
                        rankedStms: [
                            s.rankedStms[0],
                            s.rankedStms[1],
                            null, // FIXME
                            rankedBins, // middle bucket
                        ],
                        selectedBinRef: 'to_assign',
                        bins: initBins(
                            s.rankedStms[0]?.most_important.stms ?? [],
                            stepDistribution[nStm][phase2index.p11high], // Exceptional case!
                            maxPerPage
                        ),
                        // UI
                        title: t('further_ranking2', [
                            String(s.rankedStms[0]?.most_important.stms.length),
                            t('most_important').toLowerCase(),
                        ]),
                        histogramCount: maskOutOfRange(
                            6,
                            8,
                            maxBucketCapacities[s.nBucket]
                        ),
                    }
                case 'S3_11_HIGH':
                    if (nBucket !== 7) {
                        console.warn(
                            'S3_11_HIGH cannot be reached with nBucket !== 7'
                        )
                        return s
                    }

                    if (
                        promptRankMidBucket &&
                        hasMidBucketRanking === undefined
                    ) {
                        return {
                            ...s,
                            yesNoModalProps: {
                                open: true,
                                title: t('sort_middle_bucket_title'),
                                body: t('sort_middle_bucket_body'),
                                yesCta: t('yes'),
                                onYes: () => {
                                    console.debug('>>> onYes')
                                    get().setHasMidBucketRanking(true)
                                    get().onClickToNext(t) // New status !
                                },
                                noCta: t('no'),
                                onNo: () => {
                                    console.debug('>>> onNo')
                                    get().setHasMidBucketRanking(false)
                                    get().onClickToNext(t) // New status !
                                },
                                onClose: () => get().closeYesNoModal(),
                            },
                        }
                    } else if (hasMidBucketRanking === true) {
                        return {
                            status: 'S4_20_MID',
                            rankedStms: [
                                s.rankedStms[0],
                                s.rankedStms[1],
                                rankedBins,
                                null,
                            ],
                            selectedBinRef: 'to_assign',
                            bins: initBins(
                                s.rankedStms[0]?.moderately_important.stms ??
                                    [],
                                stepDistribution[nStm][phase2index.p20mid],
                                maxPerPage
                            ),
                            // UI
                            title: t('further_ranking1', [
                                String(
                                    s.rankedStms[0]?.moderately_important.stms
                                        .length
                                ),
                                t('moderately_important').toLowerCase(),
                            ]),
                            histogramCount: maskOutOfRange(
                                3,
                                5,
                                maxBucketCapacities[s.nBucket]
                            ),
                        }
                    } else {
                        // hasMidBucketRanking === false or undefined (not asked) :
                        newRankedStms = [
                            s.rankedStms[0],
                            s.rankedStms[1],
                            rankedBins,
                            null,
                        ] as QRankedStms
                        return {
                            status:
                                reflectionMode !== 0
                                    ? 'FINAL_OVERVIEW'
                                    : 'DONE_SENDING',
                            finalBuckets: hasFinalBuckets
                                ? s.finalBuckets
                                : rankedStms2buckets(newRankedStms, s.nBucket),
                            // UI
                            title: t('final_ranking'),
                            histogramCount: maxBucketCapacities[s.nBucket],
                        }
                    }
                case 'S4_20_MID':
                    if (nBucket !== 7 || !hasMidBucketRanking) {
                        console.warn(
                            'S4_20_MID cannot be reached with nBucket !== 7 or hasMidBucketRanking !== true'
                        )
                        return s
                    }
                    newRankedStms = [
                        s.rankedStms[0],
                        s.rankedStms[1],
                        s.rankedStms[2],
                        rankedBins, // middle bucket
                    ] as QRankedStms
                    let newFinalBuckets = hasFinalBuckets
                        ? s.finalBuckets
                        : rankedStms2buckets(newRankedStms, 9)
                    console.debug('>>> S4_20_MID', {
                        hasFinalBuckets,
                        reflectionMode,
                        newRankedStms,
                        newFinalBuckets,
                    })
                    return {
                        rankedStms: newRankedStms,
                        finalBuckets: newFinalBuckets,
                        status:
                            reflectionMode !== 0
                                ? 'FINAL_OVERVIEW'
                                : 'DONE_SENDING',
                        // UI
                        title: t('final_ranking'),
                        histogramCount: maxBucketCapacities[s.nBucket],
                    }
                case 'S4_11_HIGH':
                    if (nBucket !== 9) {
                        console.warn(
                            'S4_11_HIGH cannot be reached with nBucket !== 9'
                        )
                        return s
                    }
                    newRankedStms = [
                        s.rankedStms[0],
                        s.rankedStms[1],
                        rankedBins,
                        s.rankedStms[3],
                    ] as QRankedStms
                    return {
                        rankedStms: newRankedStms,
                        finalBuckets: hasFinalBuckets
                            ? s.finalBuckets
                            : rankedStms2buckets(newRankedStms, s.nBucket),
                        status:
                            reflectionMode !== 0
                                ? 'FINAL_OVERVIEW'
                                : 'DONE_SENDING',
                        // UI
                        title: t('final_ranking'),
                        histogramCount: maxBucketCapacities[s.nBucket],
                    }
                case 'FINAL_OVERVIEW': {
                    if (reflectionMode === 0) {
                        console.warn(
                            'FINAL_OVERVIEW',
                            'reflectionMode is not enabled'
                        )
                        return {
                            ...s,
                            status: 'DONE_SENDING',
                        }
                    }
                    const overfilledBuckets = getOverfilledBuckets(
                        finalBuckets,
                        effectiveNBuckets
                    )
                    const underfilledBuckets = getUnderfilledBuckets(
                        finalBuckets,
                        effectiveNBuckets
                    )
                    if (overfilledBuckets.length) {
                        return {
                            ...s,
                            yesNoModalProps: {
                                open: true,
                                title: t('final_distribution_invalid_title'),
                                body: t('final_distribution_invalid_body', [
                                    String(overfilledBuckets.length),
                                    String(underfilledBuckets.length),
                                ]),
                                yesCta: t('yes'),
                                onYes: () => {
                                    get().closeYesNoModal()
                                },
                            },
                        }
                    } else {
                        console.debug(
                            '>>> FINAL_OVERVIEW',
                            'no overfilled buckets'
                        )
                        return {
                            ...s,
                            status: 'DONE_SENDING',
                        }
                        // FIXME if 2nd validation is required:s
                        // return {
                        //     ...s,
                        //     yesNoModalProps: {
                        //         open: true,
                        //         title: t('reflect_current_view_title'),
                        //         body: t('reflect_current_view_sort_body'),
                        //         yesCta: t('yes'),
                        //         onYes: () => {
                        //             get().setRankingIsReflected(true)
                        //             get().markAs('DONE_SENDING') // useEffect.submit()
                        //         },
                        //         noCta: t('no'),
                        //         onNo: () => {
                        //             if (reflectionMode === 1) {
                        //                 // Keep updating the ranking...
                        //                 get().closeYesNoModal()
                        //             } else {
                        //                 get().setRankingIsReflected(false)
                        //                 get().markAs('DONE_SENDING') // useEffect.submit()
                        //             }
                        //         },
                        //         onClose: () => get().closeYesNoModal(),
                        //     },
                        // }
                    }
                }
                case 'DONE_SENDING':
                    console.warn('DONE_SENDING', 'already done')
                    return s
                default:
                    console.error('step out of range', fromStatus)
                    return s
            }
            return s
        })
    },
    setHasMidBucketRanking: (hasMidBucketRanking: boolean) => {
        set(() => ({
            hasMidBucketRanking,
        }))
    },

    setRankingIsReflected: (rankingIsReflected: boolean) => {
        set({ rankingIsReflected })
    },
    markAs: (status: AppStatus) => {
        set({ status })
    },
    prepareBuckets: () => {
        set((s) => ({
            finalBuckets: s.finalBuckets?.[0]?.length
                ? s.finalBuckets
                : rankedStms2buckets(s.rankedStms, s.nBucket),
        }))
    },
    onMoveStmDistro: (
        stmId: string,
        from: string,
        to: string,
        destinationIndex?: number
    ) => {
        console.log(
            'Move stm',
            stmId,
            'from',
            from,
            'to',
            to,
            'at index',
            destinationIndex
        )

        // Parse bucket indices, handle invalid inputs
        const fromMatch = from.match(/Bucket (\d+)/)
        const toMatch = to.match(/Bucket (\d+)/)

        if (!fromMatch || !toMatch) {
            console.warn('Invalid bucket format:', { from, to })
            return
        }

        const fromIndex = parseInt(fromMatch[1]) - 1
        const toIndex = parseInt(toMatch[1]) - 1

        set((s) => {
            // FIXME simplify this block of code
            const newBuckets = [...s.finalBuckets]
            const sourceBucket = newBuckets[fromIndex]
            const targetBucket = newBuckets[toIndex]

            if (!sourceBucket || !targetBucket) {
                console.warn('Invalid bucket indices:', {
                    fromIndex,
                    toIndex,
                    bucketCount: newBuckets.length,
                })
                return s
            }

            const stmIndex = sourceBucket.findIndex(
                (stm) => stm.valueKey === stmId
            )
            if (stmIndex === -1) {
                console.warn('Statement not found in source bucket:', stmId)
                return s
            }

            const [stm] = sourceBucket.splice(stmIndex, 1)
            console.log(
                'Removed statement:',
                stm.valueKey,
                'from source bucket at index:',
                stmIndex
            )

            // Handle both same-container and cross-container moves
            const insertIndex =
                destinationIndex !== undefined
                    ? destinationIndex
                    : targetBucket.length
            targetBucket.splice(insertIndex, 0, stm)
            console.log(
                'Inserted at index:',
                insertIndex,
                'same container:',
                fromIndex === toIndex
            )
            return { finalBuckets: newBuckets }
        })
    },

    setSelectedBucket: (bucket: number | null) => {
        set({ selectedBucket: bucket })
    },
    onHoverBin: (binRef: BinRefs, isIn: boolean) => {
        set((s) => ({
            highlightedBars: isIn ? getHighlightedBars(s.status, binRef) : [],
        }))
    },
    updateWarningProps: (
        props: Partial<ComponentPropsWithRef<typeof WarningMessage>> | null
    ) => {
        set({ warningProps: props })
    },
    closeYesNoModal: () => {
        console.debug('>>> closeYesNoModal')
        set({ yesNoModalProps: null })
    },
    setHasNextBtn: (hasNextBtn: boolean) => {
        set({ hasNextBtn })
    },
    setFinalBucketsRnd: () => {
        set((state) => {
            const { nBucket, stms2rank } = state
            const effectiveNBuckets =
                nBucket === 7 && state.hasMidBucketRanking === true
                    ? 9
                    : nBucket

            // Get the expected distribution for the current bucket configuration
            const fullDistribution = maxBucketCapacities[effectiveNBuckets]

            // For 7 buckets, we need to skip indices 3 and 5 (buckets 4 and 6)
            // This matches the logic in Overview.tsx
            let expectedDistribution: number[]
            if (effectiveNBuckets === 7) {
                // Use indices 0, 1, 2, 4, 6, 7, 8 (skip 3 and 5)
                expectedDistribution = [
                    fullDistribution[0], // 2
                    fullDistribution[1], // 4
                    fullDistribution[2], // 5
                    fullDistribution[4], // 20 (middle bucket)
                    fullDistribution[6], // 5
                    fullDistribution[7], // 4
                    fullDistribution[8], // 2
                ]
            } else {
                // For 9 buckets, use all buckets
                expectedDistribution = fullDistribution
            }

            // Create empty buckets array (always 9 buckets for the final structure)
            const newFinalBuckets: Assignation[][] = Array.from(
                { length: 9 },
                () => []
            )

            // Shuffle statements
            const shuffledStms = [...stms2rank].sort(() => Math.random() - 0.5)

            // Distribute statements according to expected distribution
            let stmIndex = 0
            expectedDistribution.forEach((capacity, distributionIndex) => {
                if (capacity > 0) {
                    // Map distribution index to actual bucket index
                    let bucketIndex: number
                    if (effectiveNBuckets === 7) {
                        // Map to the correct bucket indices: 0, 1, 2, 4, 6, 7, 8
                        const bucketMapping = [0, 1, 2, 4, 6, 7, 8]
                        bucketIndex = bucketMapping[distributionIndex]
                    } else {
                        // For 9 buckets, use the index directly
                        bucketIndex = distributionIndex
                    }

                    // Add the expected number of statements to this bucket
                    for (
                        let i = 0;
                        i < capacity && stmIndex < shuffledStms.length;
                        i++
                    ) {
                        newFinalBuckets[bucketIndex].push(
                            shuffledStms[stmIndex]
                        )
                        stmIndex++
                    }
                }
            })

            // If there are remaining statements (shouldn't happen with correct distribution),
            // distribute them randomly among non-zero capacity buckets
            while (stmIndex < shuffledStms.length) {
                const availableBuckets = expectedDistribution
                    .map((capacity, distributionIndex) => ({
                        capacity,
                        distributionIndex,
                    }))
                    .filter(({ capacity }) => capacity > 0)
                    .map(({ distributionIndex }) => {
                        if (effectiveNBuckets === 7) {
                            const bucketMapping = [0, 1, 2, 4, 6, 7, 8]
                            return bucketMapping[distributionIndex]
                        } else {
                            return distributionIndex
                        }
                    })

                if (availableBuckets.length > 0) {
                    const randomBucketIndex =
                        availableBuckets[
                            Math.floor(Math.random() * availableBuckets.length)
                        ]
                    newFinalBuckets[randomBucketIndex].push(
                        shuffledStms[stmIndex]
                    )
                    stmIndex++
                } else {
                    break
                }
            }

            console.debug(
                'setFinalBucketsRnd: Distributed',
                shuffledStms.length,
                'statements across',
                effectiveNBuckets,
                'buckets with mapping:',
                effectiveNBuckets === 7
                    ? [0, 1, 2, 4, 6, 7, 8]
                    : 'all 9 buckets'
            )

            return { finalBuckets: newFinalBuckets }
        })
    },
}))
